import { Failed, Succeeded } from "../enum/job-status";

/**
 * A store that can be used to persist and save a list of job IDs.
 */
export class LocalStorageJobStore {
    constructor(key) {
        this.key = key;
    }

    retrieveJobs() {
        return new Set(JSON.parse(localStorage.getItem(this.key)) || []);
    }

    updateJobs(jobIDs) {
        localStorage.setItem(this.key, JSON.stringify(Array.from(jobIDs)));
    }
}

/**
 * An abstract job tracker that allows components to subscribe to a job's status.
 */
export class JobTracker {
    /**
     * Create a new job tracker using a specific interval and persisting the tracked
     * jobs set to a specific key in local storage.
     * @param $interval {any} The angularjs interval service.
     * @param jobStore {any} A store that provides persistence for tracked jobs.
     * @param trackingInterval {number} The interval in seconds to use to poll for changes.
     */
    constructor($interval, jobStore, trackingInterval) {
        this.$interval = $interval;
        this.jobStore = jobStore;
        this.trackingInterval = trackingInterval;

        this.callbacks = [];
        /** @type {Set<string>}) */
        this.jobIDs = this.jobStore.retrieveJobs();
        this.jobs = [];
        this._initializePeriodicCheck();
    }

    /**
     * Register the given callback to be notified when there are changes to a lead batch job's status.
     */
    subscribe(cb) {
        this.callbacks.push(cb);
    }

    /**
     * Stop notifying the given callback when there is an update to a batch job's status.
     * @param {any} cb
     */
    unsubscribe(cb) {
        this.callbacks = this.callbacks.filter(function (_cb) {
            return _cb !== cb;
        });
    }

    /**
     * Get the list of all jobs being tracked by the tracker currently.
     */
    retrieveTrackedJobs() {
        throw new Error("Abstract method must be implemented by concrete subclass.");
    }

    /**
     * Track the given batch job for changes.
     * @param {any} jobID
     */
    trackNewJob(jobID) {
        this.jobIDs.add(jobID);
        this.jobStore.updateJobs(this.jobIDs);
    }

    /**
     * Stop tracking the given batch job for changes.
     * @param {any} jobID
     */
    stopTrackingJob(jobID) {
        this.jobIDs.delete(jobID);
        this.jobStore.updateJobs(this.jobIDs);
    }

    /**
     * Stop tracking any jobs in the list of jobs that are no longer in progress.
     * @param {any} jobs The list of jobs to stop tracking when finished.
     */
    stopTrackingFinishedJobs(jobs) {
        jobs.filter(job => job.JobStatus === Failed
            || job.JobStatus === Succeeded)
            .forEach(job => this.stopTrackingJob(job.Id));
    }

    _initializePeriodicCheck() {
        let isRunning = false;
        this.$interval(() => {
            if (isRunning) {
                // In case the response is slow, skip the next interval.
                return;
            }
            return this._notifySubscribersOnJobChange()
        }, this.trackingInterval)
            .catch(function (ex) {
                console.error(ex);
            })
            .then(function () {
                isRunning = false;
            })
    }

    /**
     * Return whether the two jobs are identical.
     * @param {any} a The first job in the comparison
     * @param {any} b The second job in the comparison
     */
    _jobsEqual(a, b) {
        return a.Id === b.Id
            && a.JobStatus === b.JobStatus
            && a.Messages.length === b.Messages.length // Shallow compare for messages; they should be append-only.;
    }

    _notifySubscribersOnJobChange() {
        this.retrieveTrackedJobs()
            .then(_jobs => {
                const modifiedJobs = _jobs.filter(job => {
                    const original = this.jobs[job.Id];
                    // Don't call the subscriber if this is the first time loading the job's data.
                    // The subscriber should call retrieveTrackedJobs to get their own version of the initial list.
                    return original && !this._jobsEqual(job, original);
                });
                // Update the list of jobs used for comparison.
                _jobs.forEach(job => {
                    this.jobs[job.Id] = job;
                });

                // Notify all subscribers of the change.
                if (modifiedJobs.length) {
                    this.callbacks.forEach(function (callback) {
                        callback(modifiedJobs);
                    });
                }
            });
    }
}
